import { useEffect, useState } from 'react';

import { AppIcon, LabeledFilterSelect } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ALL_FILTER_VALUE } from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import {
  Box,
  Button,
  Drawer,
  IconButton,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';

import { humanize } from './analytics.labels';
import { useAgentContext } from './AnalyticsFilterBar.agentContext';

export type FilterOption = Readonly<{ value: string; label: string }>;

export type AnalyticsFilterValues = Readonly<{
  institution: string;
  programs: string;
  grade: string;
  enrollment: string;
  coaches: string;
}>;

export type AnalyticsFilterKey = keyof AnalyticsFilterValues;

const { filters: filterStrings } = appStrings.modules.coachingAnalytics;

export const ALL_OPTION: FilterOption = { value: ALL_FILTER_VALUE, label: filterStrings.all };

/** The un-narrowed cohort. Cache keys use these values, so passing {} refetches the same data. */
export const DEFAULT_FILTERS: AnalyticsFilterValues = {
  institution: ALL_FILTER_VALUE,
  programs: ALL_FILTER_VALUE,
  grade: ALL_FILTER_VALUE,
  enrollment: ALL_FILTER_VALUE,
  coaches: ALL_FILTER_VALUE,
};

type AnalyticsFilterBarProps = Readonly<{
  /** Collapses the fields into a drawer behind a Filters button. */
  isCompact: boolean;
  /** Without this, a failed options fetch is indistinguishable from an empty cohort. */
  isOptionsUnavailable: boolean;
  filters: AnalyticsFilterValues;
  onFilterChange: (key: AnalyticsFilterKey, value: string) => void;
  /** Omitted for a coach, whose population is not an institution's. */
  institutionOptions?: readonly FilterOption[];
  programOptions: readonly FilterOption[];
  gradeOptions: readonly FilterOption[];
  enrollmentOptions: readonly FilterOption[];
  /** Omitted for a coach, who could only ever select themselves. */
  coachOptions?: readonly FilterOption[];
}>;

type FilterField = Readonly<{
  key: AnalyticsFilterKey;
  label: string;
  options: readonly FilterOption[];
  wide?: boolean;
}>;

export const AnalyticsFilterBar = ({
  isCompact,
  isOptionsUnavailable,
  filters,
  onFilterChange,
  institutionOptions,
  programOptions,
  gradeOptions,
  enrollmentOptions,
  coachOptions,
}: AnalyticsFilterBarProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Without this the drawer reopens by itself on the way back to a narrow column.
  useEffect(() => setDrawerOpen(false), [isCompact]);

  const maybeFields: readonly (FilterField | undefined)[] = [
    institutionOptions && {
      key: 'institution',
      label: filterStrings.institution,
      options: institutionOptions,
      wide: true,
    },
    { key: 'programs', label: filterStrings.programs, options: programOptions },
    { key: 'grade', label: filterStrings.grade, options: gradeOptions },
    { key: 'enrollment', label: filterStrings.enrollment, options: enrollmentOptions },
    coachOptions && { key: 'coaches', label: filterStrings.coaches, options: coachOptions },
  ];

  const fields = maybeFields.filter((field): field is FilterField => Boolean(field));

  useAgentContext(
    Object.fromEntries(
      fields.map((field) => [
        field.key,
        field.options.find((option) => option.value === filters[field.key])?.label ??
          humanize(filters[field.key]),
      ]),
    ),
  );

  const renderSelect = (field: FilterField) => (
    <LabeledFilterSelect
      id={`coaching-analytics-filter-${field.key}`}
      label={field.label}
      value={filters[field.key]}
      options={field.options}
      onChange={(value) => onFilterChange(field.key, value)}
    />
  );

  const unavailableNotice = isOptionsUnavailable ? (
    <Typography variant="text-sm-regular" sx={unavailableSx}>
      {filterStrings.unavailable}
    </Typography>
  ) : null;

  if (!isCompact) {
    return (
      <Box sx={barSx}>
        {unavailableNotice}
        <Box sx={fieldsRowSx}>
          {fields.map((field) => (
            <Box key={field.key} sx={field.wide ? wideFieldSx : fieldSx}>
              {renderSelect(field)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={barSx}>
      {unavailableNotice}
      <Button
        id="coaching-analytics-open-filters-btn"
        hierarchy="Secondary gray"
        size="md"
        sx={filtersButtonSx}
        startIcon={
          <AppIcon iconName="FilterLines" diameter="16px" color="Colors/Foreground/fg-quaternary" />
        }
        onClick={() => setDrawerOpen(true)}
      >
        {filterStrings.title}
      </Button>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: drawerPaperSx }}
      >
        <Box sx={drawerHeaderSx}>
          <Typography variant="text-lg-semibold">{filterStrings.title}</Typography>
          <IconButton
            id="coaching-analytics-close-filters-btn"
            onClick={() => setDrawerOpen(false)}
          >
            <AppIcon iconName="XClose" diameter="20px" color="Colors/Foreground/fg-quaternary" />
          </IconButton>
        </Box>
        <Box sx={drawerFieldsSx}>
          {fields.map((field) => (
            <Box key={field.key} sx={drawerFieldSx}>
              {renderSelect(field)}
            </Box>
          ))}
        </Box>
      </Drawer>
    </Box>
  );
};

const barSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  padding: backrs.spacing['spacing-lg'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  backgroundColor: backrs.colors['Beige/50'],
  '@container (min-width: 650px)': {
    padding: backrs.spacing['spacing-2xl'],
  },
});

const unavailableSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  paddingBottom: backrs.spacing['spacing-md'],
});

const fieldsRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
  '@container (min-width: 900px)': {
    flexWrap: 'nowrap',
  },
});

const filtersButtonSx: SxProps<Theme> = {
  width: '100%',
};

const drawerPaperSx: SxProps<Theme> = ({ backrs }) => ({
  width: `min(320px, calc(100vw - ${backrs.spacing['spacing-4xl']}))`,
  padding: backrs.spacing['spacing-2xl'],
});

const drawerHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: backrs.spacing['spacing-2xl'],
});

const drawerFieldsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const drawerFieldSx: SxProps<Theme> = {
  display: 'flex',
  width: '100%',
};

const fieldSx: SxProps<Theme> = {
  display: 'flex',
  minWidth: 0,
  flex: '1 1 140px',
  '@container (min-width: 900px)': {
    flex: '1 1 0',
  },
};

const wideFieldSx: SxProps<Theme> = {
  display: 'flex',
  minWidth: 0,
  flex: '1 1 100%',
  '@container (min-width: 900px)': {
    flex: '1.6 1 0',
  },
};
